import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { QueryClient } from '@tanstack/react-query';
import { RequireSession, RequireStage, AuthFailureHandler } from './lib/session';
import { Shell } from './components/Shell';
import { SiteProvider } from './lib/site';
import { ToastProvider } from './components/Toast';
import { t } from './lib/i18n';
import { LoginPage } from './pages/auth/LoginPage';
import { TotpPage } from './pages/auth/TotpPage';
import { TotpSetupPage } from './pages/auth/TotpSetupPage';
import { ChooseTenantPage } from './pages/auth/ChooseTenantPage';
import { ForgotPasswordPage, InvitePage, ResetPasswordPage } from './pages/auth/RecoveryPages';
import { GettingStarted, HomePage } from './pages/HomePage';
import { WhatsNewPage } from './pages/WhatsNewPage';
import { AppsPage } from './pages/AppsPage';
import { AccountPage } from './pages/AccountPage';
import { UsersPage } from './pages/admin/UsersPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { RolesPage } from './pages/admin/RolesPage';
import { RoleEditPage } from './pages/admin/RoleEditPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { EmailsPage } from './pages/admin/EmailsPage';
import { AuditPage } from './pages/admin/AuditPage';
import { TenantsPage } from './pages/platform/TenantsPage';
import { TenantDetailPage } from './pages/platform/TenantDetailPage';
import { PlatformDomainsPage } from './pages/platform/PlatformDomainsPage';
import { AdminsPage, JobsPage } from './pages/platform/PlatformPages';
import { PlatformSettingsPage } from './pages/platform/PlatformSettingsPage';
import { moduleRoutes } from './modules';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: false },
    },
  });
}

function Page({ area, permission, children }: { area: 'tenant' | 'platform' | 'building' | 'any'; permission?: string; children: ReactNode }) {
  return (
    <RequireSession area={area} permission={permission ?? null}>
      <SiteProvider>
        <Shell>{children}</Shell>
      </SiteProvider>
    </RequireSession>
  );
}

function NotFound() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold">{t('error.not_found')}</h1>
    </div>
  );
}

export function AppRoutes() {
  return (
    <ToastProvider>
      <AuthFailureHandler />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<RequireStage stage="mfa"><TotpPage /></RequireStage>} />
        <Route path="/login/2fa-setup" element={<RequireStage stage="mfa_setup"><TotpSetupPage /></RequireStage>} />
        <Route path="/login/choose" element={<RequireStage stage="choose_tenant"><ChooseTenantPage /></RequireStage>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite" element={<InvitePage />} />

        <Route path="/" element={<Page area="tenant"><HomePage /></Page>} />
        <Route path="/getting-started" element={<Page area="tenant"><GettingStarted /></Page>} />
        <Route path="/whats-new" element={<Page area="any"><WhatsNewPage /></Page>} />
        <Route path="/apps" element={<Page area="tenant"><AppsPage /></Page>} />
        <Route path="/platform/apps" element={<Page area="platform"><AppsPage /></Page>} />
        <Route path="/account" element={<Page area="any"><AccountPage /></Page>} />

        <Route path="/admin/users" element={<Page area="tenant" permission="users.view"><UsersPage /></Page>} />
        <Route path="/admin/users/:id" element={<Page area="tenant" permission="users.view"><UserDetailPage /></Page>} />
        <Route path="/admin/roles" element={<Page area="tenant" permission="users.view"><RolesPage /></Page>} />
        <Route path="/admin/roles/:id" element={<Page area="tenant" permission="users.view"><RoleEditPage /></Page>} />
        <Route path="/admin/settings" element={<Page area="tenant" permission="account.manage"><SettingsPage /></Page>} />
        <Route path="/admin/emails" element={<Page area="tenant" permission="account.manage"><EmailsPage /></Page>} />
        <Route path="/admin/audit" element={<Page area="tenant" permission="audit.view"><AuditPage /></Page>} />

        <Route path="/platform" element={<Navigate to="/platform/tenants" replace />} />
        <Route path="/platform/tenants" element={<Page area="platform" permission="platform.tenants_view"><TenantsPage /></Page>} />
        <Route path="/platform/tenants/:id" element={<Page area="platform" permission="platform.tenants_view"><TenantDetailPage /></Page>} />
        <Route path="/platform/domains" element={<Page area="platform" permission="platform.tenants_view"><PlatformDomainsPage /></Page>} />
        <Route path="/platform/admins" element={<Page area="platform" permission="platform.admins_manage"><AdminsPage /></Page>} />
        <Route path="/platform/settings" element={<Page area="platform" permission="platform.settings_manage"><PlatformSettingsPage /></Page>} />
        <Route path="/platform/jobs" element={<Page area="platform" permission="platform.jobs_view"><JobsPage /></Page>} />
        <Route path="/platform/audit" element={<Page area="platform" permission="platform.audit_view"><AuditPage platform /></Page>} />

        {moduleRoutes.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={r.public ? r.element : <Page area={r.area ?? 'tenant'} permission={r.permission}>{r.element}</Page>}
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </ToastProvider>
  );
}
